import { useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import InfoIcon from '@mui/icons-material/Info';
import styles from '../../../../ReviewEmployeeAccess.module.scss';
import { getAppsByUser } from '../../../../_api';
import { getPendingRequestSummary } from 'pages/AnnualUserAccessReview/_api';
import { DataTable } from '../../../../../../../../../components/UI/MDC/DataTable/DataTable';
import Spinner from '../../../../../../../../../components/UI/Spinner/Spinner';
import { EPIC_APP_ID } from 'pages/ApplicationRequest/components/containers/79/_const';
import { UAR_CONSTANTS } from 'pages/UserAccessReview/util/constants';
import { getLegacyPath } from 'routes/_utils';

const getProcessedActionChipColorHandler = action => {
	const actionChipColors = {
		MODIFY: 'warning',
		REVOKE: 'error',
	};

	return actionChipColors[action] || 'info';
};

const ProcessedActionBubble = ({ action, trackingNumber, processedAt }) => {
	const [sAnchorEl, setAnchorEl] = useState(null);
	const isPopoverOpen = Boolean(sAnchorEl);
	const actionType = String(action || 'MODIFY').toUpperCase();
	const actionLabelRaw = String(action || 'MODIFY').toLowerCase();
	const actionLabel =
		actionLabelRaw.charAt(0).toUpperCase() + actionLabelRaw.slice(1);
	const submittedAt =
		processedAt && !Number.isNaN(Date.parse(processedAt))
			? new Date(processedAt).toLocaleString()
			: '';
	const requestDetailsUrl = getLegacyPath(
		`/RRS/RequestDetails.aspx?tn=${trackingNumber}&ref=wqr`
	);

	const onBubbleClickHandler = event => {
		setAnchorEl(event.currentTarget);
	};

	const onPopoverCloseHandler = () => {
		setAnchorEl(null);
	};

	return (
		<>
			<Chip
				icon={
					<AdsClickIcon
						sx={theme => ({
							color: `${theme.palette.common.white} !important`,
						})}
					/>
				}
				size='small'
				color={getProcessedActionChipColorHandler(actionType)}
				label={actionLabel}
				onClick={onBubbleClickHandler}
				sx={theme => ({
					height: 22,
					fontSize: 11,
					cursor: 'pointer',
					color: theme.palette.common.white,
					fontWeight: 600,
					'& .MuiChip-label': {
						color: theme.palette.common.white,
					},
				})}
			/>
			<Popover
				open={isPopoverOpen}
				anchorEl={sAnchorEl}
				onClose={onPopoverCloseHandler}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<Box sx={{ p: 1.25 }}>
					<Link
						href={requestDetailsUrl}
						target='_blank'
						rel='noopener noreferrer'
						underline='hover'
					>
						{`Tracking #${trackingNumber}`}
					</Link>
					{submittedAt ? (
						<Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
							{`Submitted ${submittedAt}`}
						</Typography>
					) : null}
				</Box>
			</Popover>
		</>
	);
};

const PendingRequestIndicator = ({ onInfoClickHandler }) => {
	return (
		<Stack
			direction='row'
			spacing={0.5}
			sx={{ alignItems: 'center' }}
		>
			<Typography
				variant='caption'
				sx={theme => ({
					fontWeight: 600,
					color: theme.palette.primary.main,
				})}
			>
				Pending Request
			</Typography>
			<IconButton
				size='small'
				aria-label='pending request info'
				onClick={onInfoClickHandler}
				sx={theme => ({
					p: 0.25,
					color: theme.palette.primary.main,
				})}
			>
				<InfoIcon fontSize='small' color='primary' />
			</IconButton>
		</Stack>
	);
};

const PendingDetailRow = ({ label, value, isLast = false }) => {
	const trackingDetailsUrl =
		label === 'Tracking Number' && value
			? getLegacyPath(`/RRS/RequestDetails.aspx?tn=${value}&ref=wqr`)
			: '';

	return (
		<ListItem
			dense
			disableGutters
			sx={{
				px: 0,
				py: 1,
				borderBottom: isLast ? 'none' : '1px solid',
				borderColor: 'divider',
			}}
		>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: '140px minmax(0, 1fr)',
					columnGap: 2,
					width: '100%',
					alignItems: 'start',
				}}
			>
				<Typography variant='body2' sx={{ fontWeight: 600 }}>
					{label}
				</Typography>
				{trackingDetailsUrl ? (
					<Link
						href={trackingDetailsUrl}
						target='_blank'
						rel='noopener noreferrer'
						underline='hover'
						sx={{ wordBreak: 'break-word' }}
					>
						{value}
					</Link>
				) : (
					<Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
						{value || ''}
					</Typography>
				)}
			</Box>
		</ListItem>
	);
};

const ReviewApps = ({
	staffMember,
	index,
	formik,
	processedActionsByApp,
	onQueueAppAction,
	getQueuedAction,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [sPendingDetailsLoading, setPendingDetailsLoading] = useState(false);
	const [sPendingModal, setPendingModal] = useState(false);
	const [sPendingDetails, setPendingDetails] = useState(null);
	const staffMemberId = Number(staffMember?.fiId || staffMember?.esoFiId) || null;
	//if staffMember.apps is null or empty, get applications via api call and set the staffMember's application
	useEffect(() => {
		if (
			staffMemberId &&
			(!staffMember.applications || staffMember.applications.length === 0)
		) {
			setIsLoading(true);
			getAppsByUser(staffMemberId)
				.then(response => {
					let formikStaffMember =
						formik.values.staffMembersForReview[index];
					formikStaffMember.applications =
						response?.data?.payload || [];
					//update formik state with user applications
					let updatedFormikStaffMembers = [
						...formik.values.staffMembersForReview,
					];
					updatedFormikStaffMembers.splice(index, 1, formikStaffMember);
					formik.setFieldValue('staffMembersForReview', [
						...updatedFormikStaffMembers,
					]);
				})
				.catch(() => {})
				.finally(() => {
					setIsLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const generateAppRole = (id, value) => {
		switch (id) {
			case EPIC_APP_ID:
				const epicDescription = value.split(';');
				return (
					<span>
						{epicDescription.map(role => {
							const kvp = role.split(':');
							if (kvp.length === 2) {
								return (
									<div>
										<u>{kvp[0]}</u>: {kvp[1]}
									</div>
								);
							} else {
								return <div>{role}</div>;
							}
						})}
					</span>
				);
			default:
				return value;
		}
	};

	const onQueueActionHandler = (app, action) => {
		if (onQueueAppAction) {
			onQueueAppAction(staffMember, app, action);
		}
	};

	const onPendingPopupHandler = async app => {
		setPendingModal(true);
		setPendingDetailsLoading(true);
		setPendingDetails(null);

		const processedMarker = (() => {
			const appIdKey = String(app.id || '');
			const appNameKey = String(app.name || '').toLowerCase();
			const markerById = processedActionsByApp?.byId?.[appIdKey];
			const markerByName = processedActionsByApp?.byName?.[appNameKey];
			return markerById || markerByName || null;
		})();

		try {
			if (app?.pendingRequest) {
				const pendingStatus = await getPendingRequestSummary(
					staffMemberId,
					app.id
				);
				const requestedDate =
					pendingStatus?.RequestedDate || pendingStatus?.requestedDate || '';
				const trackingNumber =
					pendingStatus?.TrackingNumber ||
					pendingStatus?.trackingNumber ||
					app?.pendingRequestTrackingNumber ||
					'';

				setPendingDetails({
					applicationName:
						pendingStatus?.ApplicationName ||
						pendingStatus?.applicationName ||
						app?.name ||
						'',
					trackingNumber,
					status: pendingStatus?.Status || pendingStatus?.status || 'Pending',
					requestor: pendingStatus?.Requestor || pendingStatus?.requestor || '',
					requestorEsoId:
						pendingStatus?.RequestorESOID ||
						pendingStatus?.requestorEsoId ||
						'',
					requestedDate: requestedDate
						? new Date(requestedDate).toLocaleString()
						: '',
				});
				return;
			}

			if (processedMarker) {
				const currentUser = formik?.values?.currentUser || {};
				const currentUserName = `${currentUser?.lastName || ''}, ${
					currentUser?.firstName || ''
				}`.replace(/^,\s*|\s*,\s*$/g, '');

				setPendingDetails({
					applicationName: app?.name || '',
					trackingNumber: processedMarker?.trackingNumber || '',
					status: 'Submitted',
					requestor: currentUserName || currentUser?.esoFiId || '',
					requestorEsoId: String(currentUser?.esoFiId || ''),
					requestedDate:
						processedMarker?.processedAt &&
						!Number.isNaN(Date.parse(processedMarker.processedAt))
							? new Date(processedMarker.processedAt).toLocaleString()
							: '',
				});
			}
		} finally {
			setPendingDetailsLoading(false);
		}
	};

	const appTable = {
		headers: [
			{
				Value: 'Name',
				props: { sort: null, onSortChange: null },
			},
			{
				Value: 'User Name',
				props: { sort: null, onSortChange: null },
			},
			{
				Value: 'Role',
				props: { sort: null, onSortChange: null },
			},
			{
				Value: 'Actions',
				props: { sort: null, onSortChange: null },
			},
		],
		rows: staffMember.applications?.map(app => {
			const selectedAction = getQueuedAction
				? getQueuedAction(staffMemberId, app.id)
				: null;
			const processedMarker = (() => {
				const appIdKey = String(app.id || '');
				const appNameKey = String(app.name || '').toLowerCase();
				const markerById = processedActionsByApp?.byId?.[appIdKey];
				const markerByName = processedActionsByApp?.byName?.[appNameKey];
				return markerById || markerByName || null;
			})();
			const hasPendingRequest = Boolean(app?.pendingRequest);
			const isActionBlocked = hasPendingRequest || Boolean(processedMarker);

			return {
				Cells: {
					Name: {
						Value: (
							<Box
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'flex-start',
									textAlign: 'left',
									gap: 0.75,
									maxWidth: '100%',
								}}
							>
								<Typography component='span' variant='body2'>
									{app.name}
								</Typography>
								{processedMarker ? (
									<ProcessedActionBubble
										action={processedMarker.action}
										trackingNumber={processedMarker.trackingNumber}
										processedAt={processedMarker.processedAt}
									/>
								) : null}
							</Box>
						),
						Validation: {
							Key: 'true',
							Value: null,
							memberToValidateKey: null,
						},
					},
					'User Name': {
						Value: app.username,
						Validation: {
							Key: 'true',
							Value: null,
							memberToValidateKey: null,
						},
					},
					Role: {
						Value: (
							<p className={styles.cell_max_width}>
								{generateAppRole(app.id, app.role)}
							</p>
						),
						Validation: {
							Key: 'true',
							Value: null,
							memberToValidateKey: null,
						},
					},
					Actions: {
						Value: (
							isActionBlocked ? (
								<PendingRequestIndicator
									onInfoClickHandler={() => onPendingPopupHandler(app)}
								/>
							) : (
								<Stack direction='row' spacing={1}>
									<Button
										size='small'
										variant={
											selectedAction ===
											UAR_CONSTANTS.APPLICATION_MODIFY
												? 'contained'
												: 'outlined'
										}
										startIcon={<EditIcon />}
										onClick={() =>
											onQueueActionHandler(
												app,
												UAR_CONSTANTS.APPLICATION_MODIFY
											)
										}
									>
										Modify
									</Button>
									<Button
										size='small'
										variant={
											selectedAction ===
											UAR_CONSTANTS.APPLICATION_REVOKE
												? 'contained'
												: 'outlined'
										}
										startIcon={<CloseIcon />}
										onClick={() =>
											onQueueActionHandler(
												app,
												UAR_CONSTANTS.APPLICATION_REVOKE
											)
										}
									>
										Revoke
									</Button>
								</Stack>
							)
						),
						Validation: {
							Key: 'true',
							Value: null,
							memberToValidateKey: null,
						},
					},
					Key: app.id.toString(),
				},
			};
		}),
	};
	const pendingDetails = sPendingDetails || {};
	const pendingRows = [
		['Application Name', pendingDetails.applicationName],
		['Tracking Number', pendingDetails.trackingNumber],
		['Status', pendingDetails.status],
		['Requestor', pendingDetails.requestor],
		['Requestor ESOID', pendingDetails.requestorEsoId],
		['Requested Date', pendingDetails.requestedDate],
	];

	return (
		<>
			<Dialog
				open={sPendingModal}
				onClose={() => setPendingModal(false)}
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle sx={{ color: 'primary.main', fontWeight: 700 }}>
					{pendingDetails.status === 'Submitted'
						? 'Submitted Request Details'
						: 'Pending Request Details'}
				</DialogTitle>
				<DialogContent>
					{sPendingDetailsLoading ? <Spinner /> : null}
					<List dense disablePadding>
						{pendingRows.map(([label, value], index) => (
							<PendingDetailRow
								key={label}
								label={label}
								value={value}
								isLast={index === pendingRows.length - 1}
							/>
						))}
					</List>
				</DialogContent>
				<DialogActions>
					<Button variant='text' onClick={() => setPendingModal(false)}>
						Close
					</Button>
				</DialogActions>
			</Dialog>
			<div className={styles.margin_top_small}>
				<Accordion className={styles.accordion}>
					<AccordionSummary
						className={styles.accordion_summary_secondary}
						expandIcon={
							<ArrowDownwardIcon
								className={styles.accordion_summary_secondary}
							/>
						}
						aria-controls={'StaffMember-content-' + index}
						id={'StaffMember-header-' + index}
					>
						<Typography>Apps</Typography>
					</AccordionSummary>
					<AccordionDetails className={styles.accordion_details}>
						{isLoading ? (
							<Spinner />
						) : (
							<>
								{staffMember.applications &&
								staffMember.applications.length > 0 ? (
									<>
										<DataTable
											rows={appTable.rows}
											headers={appTable.headers}
										/>
									</>
								) : (
									<p>no applications found for this user</p>
								)}
							</>
						)}
					</AccordionDetails>
				</Accordion>
			</div>
		</>
	);
};

export default ReviewApps;
